const hasValue = value => value !== null && value !== undefined && value !== ""
const requiredLabelKeys = ["moment", "rating", "missed", "corrected", "interrupted", "inProgress", "completed", "first", "peak", "minimum", "last", "change", "history"]

export function normalizeSudsConfig(config = {}) {
	return {
		...config,
		trackerKind: "sudsSeries",
		labels: {...(config.labels || {})},
		points: Array.isArray(config.points) ? config.points.map(point => ({...point})) : [],
		archivedPoints: Array.isArray(config.archivedPoints) ? config.archivedPoints.map(point => ({...point})) : [],
		anchors: Array.isArray(config.anchors) ? config.anchors.map(anchor => ({...anchor})) : [],
	}
}

export function getSudsConfigErrors(config = {}) {
	const errors = []
	const min = Number(config.ratingScaleMin)
	const max = Number(config.ratingScaleMax)
	if (!Number.isInteger(min) || !Number.isInteger(max) || min < 0 || max <= min) errors.push("Invalid SUDS scale")
	for (const list of [config.points, config.anchors]) {
		if (!Array.isArray(list) || new Set(list.map(item => item.id)).size !== list.length || list.some(item => !item.id || !String(item.title || "").trim())) errors.push("SUDS rows need distinct IDs and titles")
	}
	if (!String(config.question || "").trim() || !String(config.contextLabel || "").trim()) errors.push("SUDS question and context label are required")
	if (requiredLabelKeys.some(key => !String(config.labels?.[key] || "").trim())) errors.push("SUDS display labels are required")
	if (Array.isArray(config.anchors) && config.anchors.some(anchor => !Number.isInteger(Number(anchor.value)) || Number(anchor.value) < min || Number(anchor.value) > max)) errors.push("SUDS anchors must be inside the scale")
	if (Array.isArray(config.anchors) && (!config.anchors.some(anchor => Number(anchor.value) === min) || !config.anchors.some(anchor => Number(anchor.value) === max))) errors.push("SUDS scale needs endpoint anchors")
	return errors
}

export function normalizeSudsPayload(payload = {}, config = {}) {
	const tracker = normalizeSudsConfig(config)
	return {
		...payload,
		tracker,
		series: Array.isArray(payload.series) ? payload.series.map(item => ({
			...item,
			ratings: Array.isArray(item.ratings) ? item.ratings.map(rating => ({...rating})) : [],
		})) : [],
	}
}

export function getSudsPayloadErrors(payload = {}, config = {}, isSubmission = false) {
	const errors = []
	const min = Number(config.ratingScaleMin)
	const max = Number(config.ratingScaleMax)
	const pointIds = new Set([...(config.points || []), ...(config.archivedPoints || [])].map(point => point.id))
	if (!Array.isArray(payload.series)) return ["Invalid SUDS series"]
	if (isSubmission && !payload.series.some(series => series.ratings?.some(rating => hasValue(rating.value)))) errors.push("Record at least one SUDS rating")
	for (const series of payload.series) {
		if (!String(series.target || "").trim() && series.ratings?.length) errors.push("An exposure target is required")
		if (!Array.isArray(series.ratings)) { errors.push("Invalid SUDS ratings"); continue }
		for (const rating of series.ratings) {
			if (!pointIds.has(rating.pointId)) errors.push("Unknown SUDS prompt")
			if (hasValue(rating.value) && (typeof rating.value !== "number" || !Number.isInteger(rating.value) || rating.value < min || rating.value > max)) errors.push("SUDS rating outside the configured scale")
			if (rating.status === "missed" && hasValue(rating.value)) errors.push("Missed SUDS point cannot contain a rating")
		}
	}
	return errors
}

export function summarizeSudsSeries(payload = {}, config = {}) {
	const effective = normalizeSudsPayload(payload, config)
	const points = [...effective.tracker.points, ...effective.tracker.archivedPoints, ...(payload.tracker?.points || []), ...(payload.tracker?.archivedPoints || [])]
	return effective.series.map(series => {
		const ratings = series.ratings.map(rating => ({...rating, pointTitle: points.find(point => point.id === rating.pointId)?.title || "Removed prompt", pointInstructions: points.find(point => point.id === rating.pointId)?.instructions || ""}))
		const displayRatings = effective.tracker.points.flatMap(point => {
			const matches = ratings.filter(rating => rating.pointId === point.id)
			return matches.length ? matches : [{id: `gap-${point.id}`, pointId: point.id, pointTitle: point.title, pointInstructions: point.instructions || "", value: null, status: "unanswered"}]
		}).concat(ratings.filter(rating => !effective.tracker.points.some(point => point.id === rating.pointId)))
		const observed = ratings.filter(rating => hasValue(rating.value) && Number.isFinite(Number(rating.value)))
		const first = observed[0] || null
		const last = observed[observed.length - 1] || null
		return {
			id: series.id,
			target: series.target || "",
			status: series.status || "inProgress",
			scaleMin: payload.tracker?.ratingScaleMin ?? effective.tracker.ratingScaleMin,
			scaleMax: payload.tracker?.ratingScaleMax ?? effective.tracker.ratingScaleMax,
			anchors: (payload.tracker?.anchors ?? effective.tracker.anchors).map(anchor => {
				const current = effective.tracker.anchors.find(item => item.id === anchor.id && item.value === anchor.value)
				return {...anchor, title: current?.title ?? anchor.title}
			}),
			ratings,
			displayRatings,
			first,
			last,
			peak: observed.length ? Math.max(...observed.map(rating => Number(rating.value))) : null,
			minimum: observed.length ? Math.min(...observed.map(rating => Number(rating.value))) : null,
			observedChange: observed.length >= 2 ? Number(last.value) - Number(first.value) : null,
		}
	})
}
